/**
 * ConfirmDialog — in-app replacement for window.confirm / window.prompt.
 *
 * Native browser dialogs ("dockscheduler.symbia.com says…") break the visual
 * flow of the app, so all confirmations and short reason prompts route through
 * this component instead. Supports an optional required reason field (used by
 * the reject-request flow, where the carrier receives the reason by email).
 */
import { useEffect, useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

export interface ConfirmDialogProps {
    open: boolean;
    title: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    destructive?: boolean;
    /** When true, a required free-text field is shown and passed to onConfirm. */
    requireReason?: boolean;
    reasonLabel?: string;
    reasonPlaceholder?: string;
    busy?: boolean;
    onConfirm: (reason?: string) => void;
    onCancel: () => void;
}

export function ConfirmDialog({
    open,
    title,
    message,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    destructive = false,
    requireReason = false,
    reasonLabel = 'Reason',
    reasonPlaceholder = '',
    busy = false,
    onConfirm,
    onCancel,
}: ConfirmDialogProps) {
    const [reason, setReason] = useState('');

    // Clear the field each time the dialog (re)opens so a prior value never leaks.
    useEffect(() => {
        if (open) setReason('');
    }, [open]);

    const reasonOk = !requireReason || reason.trim().length > 0;

    return (
        <Dialog open={open} onOpenChange={(o) => !o && !busy && onCancel()}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    {/* ⚠️ `whitespace-pre-line` SO A MULTI-LINE MESSAGE RENDERS AS
                        WRITTEN. HTML collapses newlines, so a caller that builds a
                        bulleted list — the upload back-out lists every appointment it
                        REFUSED to delete — would have it flattened into one run-on
                        paragraph, which is exactly the part nobody would then read.
                        ⭐ Harmless for the single-line callers: there is nothing to
                        preserve. */}
                    <DialogDescription className="whitespace-pre-line">{message}</DialogDescription>
                </DialogHeader>

                {requireReason && (
                    <div className="space-y-1.5">
                        <Label htmlFor="confirm-reason">{reasonLabel}</Label>
                        <Textarea
                            id="confirm-reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder={reasonPlaceholder}
                            rows={3}
                            autoFocus
                        />
                    </div>
                )}

                <DialogFooter>
                    <div className="flex gap-2 justify-end w-full">
                        <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>
                            {cancelLabel}
                        </Button>
                        <Button
                            variant={destructive ? 'destructive' : 'default'}
                            size="sm"
                            onClick={() => onConfirm(requireReason ? reason.trim() : undefined)}
                            disabled={busy || !reasonOk}
                        >
                            {confirmLabel}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
